<script setup lang="ts">
import { type PropType, watch, computed } from 'vue'
import { useVModels, createReusableTemplate } from '@vueuse/core'
import {
	Dialog,
	DialogContent,
	DialogClose,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from '@/components/ui/dialog'
import {
	Sheet,
	SheetContent,
	SheetClose,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import { useBreakpoints, getCouponEventExtra } from '@/lib/utils'
import CouponCard from '@/components/CouponCard.vue'
import type { ICoupon } from '@/types/Coupon'
import { createGAEvent, GaGeneralEvent } from '@/assets/ts/customEvent'

defineOptions({
	inheritAttrs: false,
})

const props = defineProps({
	isOpen: {
		type: Boolean,
		default: false,
	},
	couponCardData: {
		type: Object as PropType<ICoupon | null>,
		default: null,
	},
	couponCardText: {
		type: Object,
		default: () => ({}),
	},
	usage: {
		type: String,
		default: '',
	},
	currentPageName: {
		type: String,
		default: 'Coupon Detail Popup Page',
	},
})

const emit = defineEmits(['update:isOpen'])

const { isOpen } = useVModels(props, emit)
const { isTabletPortrait } = useBreakpoints()
const [DefineModalContent, ReuseModalContent] = createReusableTemplate()

const scrollBarClasses = [
	'[scrollbar-gutter:stable]',
	'[&::-webkit-scrollbar]:w-[12px] [&::-webkit-scrollbar]:bg-transparent',
	'[&::-webkit-scrollbar-thumb]:bg-n-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-clip-padding',
	'[&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-[4px] [&::-webkit-scrollbar-thumb]:border-transparent',
]

const couponEventExtra = computed(() => {
	if (!props.couponCardData) return 'unknown'
	return getCouponEventExtra(
		props.couponCardData.order_type,
		props.couponCardData.code,
	)
})

watch(isOpen, newVal => {
	if (newVal) {
		createGAEvent(GaGeneralEvent.PageLoad, {
			page_name: 'Coupon Detail Popup Page',
			event_extra: couponEventExtra.value,
		})
	}
})
</script>

<template>
	<DefineModalContent>
		<div
			v-if="couponCardData"
			class="mx-4px mt-20px max-h-full overflow-y-auto mdp:my-24px"
			:class="scrollBarClasses"
		>
			<div class="px-16px mdp:h-[min(calc(80dvh),552px)] mdp:px-20px">
				<CouponCard
					:order-type="couponCardData.order_type"
					:discount-type="couponCardData.discount_type"
					:title="couponCardData.title"
					:coupon-code="couponCardData.code"
					:coupon-amount="couponCardData.coupon_amount"
					:percentage-discount="couponCardData.percentage_discount"
					:start-date="couponCardData.start_date"
					:expiry-date="couponCardData.expiry_date"
					:is-expire-soon="couponCardData.is_expire_soon"
					:terms="couponCardData.terms"
					:text="couponCardText"
					:is-in-modal="true"
					:current-page-name="currentPageName"
				/>

				<section class="mb-16px mt-20px">
					<div class="typography-s1-str mb-8px">
						{{ couponCardText.coupon_details_how_to_use }}
					</div>
					<div
						class="typography-s2-reg whitespace-pre-line break-all text-n-500"
					>
						{{ usage }}
					</div>
				</section>

				<section class="pb-distant-acquaintances">
					<div class="typography-s1-str mb-8px">
						{{ couponCardText.coupon_details_terms_conditions }}
					</div>
					<div
						class="typography-s2-reg whitespace-pre-line break-all text-n-500"
					>
						{{ couponCardData.terms }}
					</div>
				</section>
			</div>
		</div>
	</DefineModalContent>

	<Dialog v-if="isTabletPortrait" v-model:open="isOpen">
		<DialogContent
			class="shadow-[0px_4px_10px_0px_rgba(0,0,0,0.10) max-h-[80dvh] max-w-[min(calc(100dvw-64px),796px)] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-lg border-none bg-white text-n-950"
		>
			<DialogHeader class="py-16px pl-24px pr-20px">
				<DialogTitle
					class="typography-h5-str flex items-center justify-between text-left"
				>
					<div>{{ couponCardText.coupon_details_title }}</div>
					<DialogClose>
						<BaseIcon name="close_v1" class="h-24px w-24px" />
					</DialogClose>
				</DialogTitle>

				<DialogDescription></DialogDescription>
			</DialogHeader>

			<ReuseModalContent />
		</DialogContent>
	</Dialog>

	<Sheet v-else v-model:open="isOpen">
		<SheetContent
			side="bottom"
			class="shadow-[0px_4px_10px_0px_rgba(0,0,0,0.10) rounded-t-lg border-none bg-white text-n-950"
		>
			<div class="flex h-[calc(85dvh)] flex-col">
				<SheetHeader class="py-12px pl-20px pr-16px">
					<SheetTitle
						class="typography-h5-str flex items-center justify-between gap-16px text-center"
					>
						<div>
							{{ couponCardText.coupon_details_title }}
						</div>
						<SheetClose>
							<BaseIcon name="close_v1" class="h-24px w-24px" />
						</SheetClose>
					</SheetTitle>
				</SheetHeader>

				<ReuseModalContent />
			</div>
		</SheetContent>
	</Sheet>
</template>
